import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type mapboxgl from 'mapbox-gl'
import { useAdminCoverage, type CoverageChurch } from '../../hooks/useAdmin'
import { MAPBOX_TOKEN as TOKEN } from '../../lib/geocode'
import { reportMapWebglFallback } from '../../lib/mapWebgl'
import { X } from 'lucide-react'

const STATUS_COLORS: Record<CoverageChurch['status'], string> = {
  claimed:    '#4F5E32',
  active:     '#22c55e',
  stale:      '#f59e0b',
  discovered: '#94a3b8',
  bare:       '#cbd5e1',
}

const STATUS_LABELS: Record<CoverageChurch['status'], string> = {
  claimed:    'Claimed',
  active:     'Active',
  stale:      'Stale',
  discovered: 'Discovered',
  bare:       'Bare',
}

type StatusKey = CoverageChurch['status']
const ALL_STATUSES: StatusKey[] = ['claimed', 'active', 'stale', 'discovered', 'bare']

function makeCircleGeoJSON(lat: number, lng: number, radiusKm: number) {
  const n = 64
  const coords: [number, number][] = []
  for (let i = 0; i <= n; i++) {
    const angle = (2 * Math.PI * i) / n
    const dlat = (radiusKm / 111.32) * Math.sin(angle)
    const dlng = (radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180))) * Math.cos(angle)
    coords.push([lng + dlng, lat + dlat])
  }
  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        geometry: { type: 'Polygon' as const, coordinates: [coords] },
        properties: {},
      },
    ],
  }
}

export default function AdminCoverageMapPage() {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const pinMarkerRef = useRef<mapboxgl.Marker | null>(null)
  const navigate = useNavigate()

  const { data, isLoading } = useAdminCoverage()
  const [mapLoaded, setMapLoaded] = useState(false)
  // Runtime WebGL init failure → show a small placeholder in place of the map.
  const [webglFailed, setWebglFailed] = useState(false)
  const [visibleStatuses, setVisibleStatuses] = useState<Set<StatusKey>>(new Set(ALL_STATUSES))
  const [pinnedPoint, setPinnedPoint] = useState<{ lat: number; lng: number } | null>(null)
  const [discoverRadius, setDiscoverRadius] = useState(50)

  const churches = useMemo(() => data?.data ?? [], [data])

  const counts = ALL_STATUSES.reduce((acc, s) => {
    acc[s] = churches.filter((c) => c.status === s).length
    return acc
  }, {} as Record<StatusKey, number>)

  // GeoJSON for the currently visible churches — fed into the clustered source
  const visibleGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: churches
      .filter((c) => visibleStatuses.has(c.status))
      .map((c) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [c.lng, c.lat] as [number, number] },
        properties: { name: c.name, status: c.status },
      })),
  }), [churches, visibleStatuses])

  // Init map — add clustered GeoJSON source + WebGL layers (no DOM markers)
  useEffect(() => {
    if (!mapContainerRef.current || !TOKEN) return
    let cancelled = false

    // Any throw here (most commonly `Failed to initialize WebGL` from `new mb.Map`)
    // must be caught, not left as an unhandled rejection — show the placeholder.
    const handleInitError = (err: unknown) => {
      if (cancelled) return
      reportMapWebglFallback(err)
      setWebglFailed(true)
    }

    void import('mapbox-gl').then((mod) => {
      if (cancelled || !mapContainerRef.current) return
      try {
      const mb = mod.default
      mb.accessToken = TOKEN

      const map = new mb.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-98.5795, 39.8283],
        zoom: 4,
        attributionControl: true,
      })

      map.on('load', () => {
        if (cancelled) return

        // ── Source (empty until data arrives) ─────────────────────────────
        map.addSource('churches', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          cluster: true,
          clusterMaxZoom: 10,
          clusterRadius: 40,
        })

        // ── Cluster bubbles ────────────────────────────────────────────────
        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'churches',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step', ['get', 'point_count'],
              '#94a3b8', 10,
              '#f59e0b', 50,
              '#ef4444',
            ],
            'circle-radius': [
              'step', ['get', 'point_count'],
              14, 10,
              18, 50,
              22,
            ],
            'circle-opacity': 0.88,
          },
        })

        // ── Cluster count labels ───────────────────────────────────────────
        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'churches',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 11,
          },
          paint: { 'text-color': '#ffffff' },
        })

        // ── Individual church dots ─────────────────────────────────────────
        map.addLayer({
          id: 'unclustered-point',
          type: 'circle',
          source: 'churches',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': ['match', ['get', 'status'],
              'claimed',    STATUS_COLORS.claimed,
              'active',     STATUS_COLORS.active,
              'stale',      STATUS_COLORS.stale,
              'discovered', STATUS_COLORS.discovered,
              'bare',       STATUS_COLORS.bare,
              '#94a3b8',
            ],
            'circle-radius': 5,
            'circle-stroke-width': 1.5,
            'circle-stroke-color': 'rgba(255,255,255,0.8)',
            'circle-opacity': 0.9,
          },
        })

        // ── Pointer cursor on hover ────────────────────────────────────────
        for (const layer of ['clusters', 'unclustered-point']) {
          map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer' })
          map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = '' })
        }

        // ── Click cluster → zoom in ────────────────────────────────────────
        map.on('click', 'clusters', (e) => {
          const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
          if (!features.length) return
          const clusterId = features[0].properties?.cluster_id as number
          const source = map.getSource('churches') as mapboxgl.GeoJSONSource
          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err || zoom == null) return
            const coords = (features[0].geometry as unknown as { coordinates: [number, number] }).coordinates
            map.easeTo({ center: coords, zoom })
          })
        })

        // ── Click church dot → popup ───────────────────────────────────────
        map.on('click', 'unclustered-point', (e) => {
          const feat = e.features?.[0]
          if (!feat) return
          const coords = (feat.geometry as unknown as { coordinates: [number, number] }).coordinates
          const { name, status } = feat.properties as { name: string; status: StatusKey }
          new mb.Popup({ closeButton: false, offset: 10, maxWidth: '220px' })
            .setLngLat(coords)
            .setHTML(`
              <div style="font-family:sans-serif;font-size:12px;line-height:1.5">
                <div style="font-weight:600;font-size:13px;margin-bottom:2px">${name}</div>
                <div style="color:#64748b">${STATUS_LABELS[status] ?? status}</div>
              </div>
            `)
            .addTo(map)
        })

        // ── Click map background → set discover pin ────────────────────────
        map.on('click', (e) => {
          const hits = map.queryRenderedFeatures(e.point, { layers: ['clusters', 'unclustered-point'] })
          if (hits.length) return // let cluster/church handlers handle it
          const { lat, lng } = e.lngLat
          setPinnedPoint({ lat, lng })
        })

        mapRef.current = map
        map.resize()
        setMapLoaded(true)
      })
      } catch (err) {
        handleInitError(err)
      }
    }).catch(handleInitError)

    return () => {
      cancelled = true
      pinMarkerRef.current?.remove()
      pinMarkerRef.current = null
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [])

  // Push updated GeoJSON into the source whenever churches or visibility changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return
    ;(map.getSource('churches') as mapboxgl.GeoJSONSource | undefined)?.setData(visibleGeoJSON)
  }, [visibleGeoJSON, mapLoaded])

  // Draw/update circle + pin marker when pinnedPoint or radius changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    void import('mapbox-gl').then((mod) => {
      const mb = mod.default

      if (map.getLayer('discover-circle-fill')) map.removeLayer('discover-circle-fill')
      if (map.getLayer('discover-circle-stroke')) map.removeLayer('discover-circle-stroke')
      if (map.getSource('discover-circle')) map.removeSource('discover-circle')

      pinMarkerRef.current?.remove()
      pinMarkerRef.current = null

      if (!pinnedPoint) return

      const { lat, lng } = pinnedPoint

      map.addSource('discover-circle', {
        type: 'geojson',
        data: makeCircleGeoJSON(lat, lng, discoverRadius),
      })
      map.addLayer({
        id: 'discover-circle-fill',
        type: 'fill',
        source: 'discover-circle',
        paint: { 'fill-color': '#3B4CCA', 'fill-opacity': 0.1 },
      })
      map.addLayer({
        id: 'discover-circle-stroke',
        type: 'line',
        source: 'discover-circle',
        paint: { 'line-color': '#3B4CCA', 'line-width': 2, 'line-opacity': 0.65 },
      })

      const el = document.createElement('div')
      el.style.cssText = `
        width: 14px; height: 14px; border-radius: 50%;
        background: #3B4CCA; border: 2.5px solid #fff;
        box-shadow: 0 2px 8px rgba(59,76,202,0.45);
        cursor: default;
      `
      el.addEventListener('click', (e) => e.stopPropagation())
      pinMarkerRef.current = new mb.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map)
    })
  }, [pinnedPoint, discoverRadius, mapLoaded])

  function toggleStatus(s: StatusKey) {
    setVisibleStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  }

  function handleOpenDiscover() {
    if (!pinnedPoint) return
    navigate('/admin/churches', {
      state: {
        discoverLat: pinnedPoint.lat,
        discoverLng: pinnedPoint.lng,
        discoverRadius,
      },
    })
  }

  return (
    <div style={{ position: 'fixed', left: 224, top: 0, right: 0, bottom: 0 }}>
      {!TOKEN ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 13, color: '#94a3b8' }}>
          Mapbox token not configured
        </div>
      ) : webglFailed ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 13, color: '#94a3b8' }}>
          Map unavailable in this browser
        </div>
      ) : (
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
      )}

      {/* Floating legend */}
      <div style={{
        position: 'absolute', top: 16, left: 16, zIndex: 10,
        background: 'rgba(255,255,255,0.96)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0,0,0,0.07)',
        borderRadius: 12,
        padding: '12px 14px',
        minWidth: 196,
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
      }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 3 }}>Coverage</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 10, fontFamily: 'Georgia, serif', letterSpacing: '-0.01em' }}>Church map</div>

        {isLoading ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading…</div>
        ) : (
          <>
            {ALL_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 7px', borderRadius: 7, border: 'none',
                  cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: 'inherit',
                  background: visibleStatuses.has(s) ? 'rgba(0,0,0,0.04)' : 'transparent',
                  opacity: visibleStatuses.has(s) ? 1 : 0.3,
                  marginBottom: 1,
                }}
              >
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_COLORS[s], display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#334155', flex: 1 }}>{STATUS_LABELS[s]}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', fontFamily: 'ui-monospace,monospace' }}>{counts[s]}</span>
              </button>
            ))}
            <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 7, paddingLeft: 3 }}>
              {churches.length} total · click to toggle
            </div>
          </>
        )}
      </div>

      {!pinnedPoint && (
        <div style={{
          position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.55)', color: '#fff', backdropFilter: 'blur(4px)',
          borderRadius: 20, padding: '6px 16px', fontSize: 12, pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          Click anywhere on the map to set a discovery area
        </div>
      )}

      {pinnedPoint && (
        <div style={{
          position: 'absolute', bottom: 32, right: 32, zIndex: 10,
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 12, padding: '14px 16px', width: 248,
          boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Discover this area</div>
            <button
              type="button"
              onClick={() => setPinnedPoint(null)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 3, color: '#94a3b8', display: 'flex', borderRadius: 4 }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace,monospace', marginBottom: 14 }}>
            {pinnedPoint.lat.toFixed(4)}, {pinnedPoint.lng.toFixed(4)}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>Radius</span>
            <input
              type="range"
              min={5} max={200} step={5}
              value={discoverRadius}
              onChange={(e) => setDiscoverRadius(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#3B4CCA' }}
            />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', fontFamily: 'ui-monospace,monospace', width: 44, textAlign: 'right' }}>
              {discoverRadius} km
            </span>
          </div>

          <button
            type="button"
            onClick={handleOpenDiscover}
            style={{
              width: '100%', marginTop: 10, padding: '9px 0', borderRadius: 8,
              background: '#3B4CCA', color: '#fff',
              border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            Open in Discover →
          </button>
        </div>
      )}
    </div>
  )
}
